import React from 'react';
import { Route, Switch } from 'react-router-dom';
import { connect, useSelector } from 'react-redux';
import type { AppState } from '~/reducers';
import TrackingRedirect from '~/components/views/tracking/TrackingRedirect/TrackingRedirect';
import withAuthentication from '~/components/containers/WithAuthentication/WithAuthentication';
import WebSocketReceiver from '~/components/views/WebSocketReceiver/WebSocketReceiver';
import TagRelationshipIsLoadingWarning from '~/ducks/asset-tag-relationships/components/TagRelationshipIsLoadingWarning';
import YoutubePlaylistContainer from '~/components/containers/YoutubePlaylistContainer/YoutubePlaylistContainer';
import BuySensorModalContainer from '~/components/containers/BuySensorModalContainer/BuySensorModalContainer';
import ChangeLogModalContainer from '~/domain/changelog/components/ChangeLogModalContainer/ChangeLogModalContainer';
import YoutubeConstants from '~/constants/youtube-constants';
import { getRouteComponentsForUser } from '~/components/containers/SecuredAppContainer/utils';
import { useGeneralTranslationsWrapper } from '~/core/hooks/use-general-translations-wrapper';
import useWsConnector from '~/hooks/use-ws-connector';
import { useAssetsTypesCounts } from '~/domain/assets/hooks/use-assets-types-counts';
import AssetOperationStatusObserver from '~/domain/assets/components/AssetOperationStatusObserver/AssetOperationStatusObserver';
import useSyncFilterOptions from '~/domain/inventory-list/hooks/use-sync-filter-options';
import { useUptime } from '~/domain/uptime/hooks/use-uptime';
import NativeSwitch from '~/domain/native/components/NativeSwitch';
import { useUptimeDisabledWsToast } from '~/domain/uptime/hooks/use-uptime-disabled-ws-toast';
import { useRouterAnalytics } from '~/domain/analytics/hooks/use-router-analytics';
import ActivityTracker from '~/domain/robotic-mowers/components/ActivityTracker/ActivityTracker';
import IncidentBanner from '~/core/components/incident-communication/components/IncidentBanner/IncidentBanner';
import { StatusMode } from '~/core/components/incident-communication/models/AppStatusConfig';
import ApplicationHeader from './components/ApplicationHeader/ApplicationHeader';
import * as S from './styled';

export interface StateProps {
  youtubeModalIsOpen: boolean;
  buySensorModalIsOpen: boolean;
  landingPage: string;
}

export const SecuredAppContainer: React.FC<StateProps> = (props) => {
  useWsConnector();
  useUptimeDisabledWsToast();
  useAssetsTypesCounts();
  useSyncFilterOptions();
  useRouterAnalytics();
  useGeneralTranslationsWrapper();
  const { isUptime } = useUptime();
  const onWebView = useSelector((state: AppState) => state.native.onWebView);
  const enabledRoutes = getRouteComponentsForUser();
  return (
    <>
      {!onWebView && <IncidentBanner statusMode={StatusMode.SecuredMaintenanceBanner} />}
      <WebSocketReceiver />
      <AssetOperationStatusObserver />
      <TagRelationshipIsLoadingWarning />
      {props.buySensorModalIsOpen && <BuySensorModalContainer />}
      {!isUptime && <ChangeLogModalContainer />}
      {props.youtubeModalIsOpen && (
        <YoutubePlaylistContainer playlistId={YoutubeConstants.FLEET_PLAYLIST_ID} />
      )}
      <ApplicationHeader />
      <ActivityTracker />
      <S.MainContent $onWebview={!!onWebView}>
        {onWebView ? (
          <NativeSwitch />
        ) : (
          <Switch>
            {enabledRoutes}
            <Route
              key="redirect-route"
              render={() => {
                return (
                  <TrackingRedirect
                    to={props.landingPage}
                    tracking={{
                      label: 'User is not authorized to view route',
                    }}
                  />
                );
              }}
            />
          </Switch>
        )}
      </S.MainContent>
    </>
  );
};

const mapStateToProps = ({ authentication, buySensor, youtubePlaylist }: AppState): StateProps => {
  return {
    landingPage: authentication.landingPage,
    buySensorModalIsOpen: buySensor.isModalOpen,
    youtubeModalIsOpen: youtubePlaylist.modalIsOpen,
  };
};

export default withAuthentication(connect(mapStateToProps)(SecuredAppContainer), false);
